/**
 * Rewards Service
 * 
 * Handles GraphQL queries related to rewards data
 */

/**
 * Interface for the daily rewards data
 */
export interface RewardsDailyData {
  ts: string;
  amount: number;
  wallet: string;
}

/**
 * Interface for the rewards data response from the API
 */
export interface GatewayRewardsData {
  betaInfo: {
    total: number;
  };
  betaDaily: Array<{
    gwId: string;
    sn: string;
    rewards: RewardsDailyData[];
  }>;
}

/**
 * Interface for the graph data item
 */
export interface RewardsGraphDataItem {
  date: string;
  rewards: number;
  wallet: string;
  gwId?: string;  // Gateway ID
  derDetails?: {
    [sn: string]: number;  // DER serial number to rewards amount mapping
  };
  isSampleData?: boolean;  // Flag to indicate if this is sample data
}

/**
 * Interface for the top gateways by rewards
 */
export interface TopGatewayRewards {
  gwId: string;
  amount: number;
  name: string;
}

/**
 * Interface for the top wallets by rewards
 */
export interface TopWalletRewards {
  wallet: string;
  amount: number;
  numerOfGateways: number;
}

/**
 * Enum for time period selection
 */
export enum RewardsTimePeriod {
  CURRENT_MONTH = 'CURRENT_MONTH',
  LAST_FULL_WEEK = 'LAST_FULL_WEEK'
}

/**
 * Fetches rewards data for a specific gateway and wallet
 * If gatewayId is empty, fetches total rewards for the wallet across all gateways
 * 
 * @param gatewayId The ID of the gateway (can be empty to get all wallet rewards)
 * @param walletAddress The wallet address
 * @returns The rewards data or null if there was an error
 */
export const fetchGatewayRewards = async (
  gatewayId: string,
  walletAddress: string
): Promise<GatewayRewardsData | null> => {
  try {
    // Different query based on whether a gatewayId is provided
    let queryStr;
    
    if (gatewayId) {
      // Query for a specific gateway
      queryStr = `
        query daily_rewards {
          token {
            rewards {
              betaInfo(wallet:"${walletAddress}", gwId:"${gatewayId}") {
                total
              }
              betaDaily(gwId:"${gatewayId}") {
                gwId
                sn
                rewards {
                  ts
                  amount
                  wallet
                }
              }
            }
          }
        }
      `;
    } else {
      // Query for total wallet rewards across all gateways
      queryStr = `
        query wallet_rewards {
          token {
            rewards {
              betaInfo(wallet:"${walletAddress}") {
                total
              }
              betaDaily(wallet:"${walletAddress}") {
                gwId
                sn
                rewards {
                  ts
                  amount
                  wallet
                }
              }
            }
          }
        }
      `;
    }

    console.log(`Sending GraphQL query for wallet ${walletAddress.slice(0, 8)}... ${gatewayId ? `and gateway ${gatewayId}` : 'across all gateways'}`);
    
    const query = JSON.stringify({ query: queryStr });
    
    const response = await fetch('https://api.srcful.dev/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: query
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      throw new Error(result.errors[0].message);
    }

    // Check if the response has the expected structure
    if (!result.data || !result.data.token || !result.data.token.rewards) {
      console.error('Unexpected API response structure:', JSON.stringify(result, null, 2));
      throw new Error('Unexpected API response structure');
    }

    // Log the actual response data to debug
    console.log('API Response:', {
      hasTotal: result.data.token.rewards.betaInfo?.total !== undefined,
      totalRewards: result.data.token.rewards.betaInfo?.total,
      hasBetaDaily: Array.isArray(result.data.token.rewards.betaDaily),
      betaDailyLength: Array.isArray(result.data.token.rewards.betaDaily) ? result.data.token.rewards.betaDaily.length : 0
    });

    // Create a properly formatted GatewayRewardsData object
    const formattedResponse: GatewayRewardsData = {
      betaInfo: result.data.token.rewards.betaInfo || { total: 0 },
      betaDaily: result.data.token.rewards.betaDaily || []
    };

    return formattedResponse;
  } catch (error) {
    console.error('Error fetching rewards:', error);
    return null;
  }
};

/**
 * Processes the raw rewards data into graph-friendly format
 * Aggregates rewards across all DERs for each date
 * 
 * @param rewardsData The raw rewards data from the API
 * @returns Formatted data ready for graphing
 */
export const processRewardsForGraph = (
  rewardsData: GatewayRewardsData | null
): RewardsGraphDataItem[] => {
  if (!rewardsData || !rewardsData.betaDaily || rewardsData.betaDaily.length === 0) {
    return [];
  }

  // Create a map to aggregate rewards by date
  const dailyAggregates: Map<string, {
    total: number;
    wallet: string;
    derDetails: { [sn: string]: number };
    gwId: string;
    isRealData: boolean;
  }> = new Map();

  // Process all DERs for the gateway
  rewardsData.betaDaily.forEach(der => {
    der.rewards.forEach(reward => {
      // Skip entries with zero rewards and null wallet
      if (reward.amount === 0 && reward.wallet === null) {
        return;
      }

      const dateKey = reward.ts;
      const existingDay = dailyAggregates.get(dateKey);

      if (existingDay) {
        // Add to existing day's total
        existingDay.total += reward.amount;
        // Store individual DER amount if it's not zero
        if (reward.amount > 0) {
          existingDay.derDetails[der.sn || 'unknown'] = reward.amount;
        }
      } else {
        // Create new day entry
        dailyAggregates.set(dateKey, {
          total: reward.amount,
          wallet: reward.wallet || '',
          derDetails: reward.amount > 0 ? { [der.sn || 'unknown']: reward.amount } : {},
          gwId: der.gwId,
          isRealData: true  // Mark as real data from API
        });
      }
    });
  });

  // Convert to array format
  const dailyRewards = Array.from(dailyAggregates.entries()).map(([date, data]) => ({
    date,
    rewards: data.total,
    wallet: data.wallet,
    derDetails: data.derDetails,
    gwId: data.gwId,
    // Never mark real API data as sample data
    isSampleData: false  
  }));
  
  // Sort by date ascending
  dailyRewards.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  
  return dailyRewards;
};

/**
 * Calculates total rewards earned by the specified wallet from gateway data
 * 
 * @param rewardsData The rewards data
 * @param walletAddress The wallet address to calculate rewards for
 * @returns The total rewards amount
 */
export const calculateTotalRewards = (
  rewardsData: GatewayRewardsData | null,
  walletAddress: string
): number => {
  // If betaInfo is available and non-zero, use it
  if (rewardsData?.betaInfo?.total) {
    return rewardsData.betaInfo.total;
  }
  
  // Otherwise calculate from all daily data
  if (!rewardsData || !rewardsData.betaDaily || rewardsData.betaDaily.length === 0) {
    return 0;
  }
  
  // Sum up all rewards across all days and all gateways
  return rewardsData.betaDaily.reduce((totalSum, gateway) => {
    const gatewaySum = gateway.rewards
      .filter(reward => reward.wallet === walletAddress)
      .reduce((sum, reward) => sum + reward.amount, 0);
    return totalSum + gatewaySum;
  }, 0);
};

/**
 * Fetches top gateways by rewards for a specific time period
 * 
 * @param timePeriod The time period to fetch data for
 * @returns The top gateways data or null if there was an error
 */
export const fetchTopGatewaysByRewards = async (
  timePeriod: RewardsTimePeriod
): Promise<TopGatewayRewards[] | null> => {
  try {
    const queryStr = `
      query top_gateways {
        stats {
          betaRewardsTopListByGw(timePeriod: ${timePeriod}) {
            gwId
            amount
            name
          }
        }
      }
    `;

    const query = JSON.stringify({ query: queryStr });

    const response = await fetch('https://api.srcful.dev/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: query
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    return result.data.stats.betaRewardsTopListByGw;
  } catch (error) {
    console.error('Error fetching top gateways by rewards:', error);
    return null;
  }
};

/**
 * Fetches top wallets by rewards for a specific time period
 * 
 * @param timePeriod The time period to fetch data for
 * @returns The top wallets data or null if there was an error
 */
export const fetchTopWalletsByRewards = async (
  timePeriod: RewardsTimePeriod
): Promise<TopWalletRewards[] | null> => {
  try {
    const queryStr = `
      query top_wallets {
        stats {
          betaRewardsTopListByWallet(timePeriod: ${timePeriod}) {
            wallet
            amount
            numerOfGateways
          }
        }
      }
    `;

    const query = JSON.stringify({ query: queryStr });

    const response = await fetch('https://api.srcful.dev/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: query
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    return result.data.stats.betaRewardsTopListByWallet;
  } catch (error) {
    console.error('Error fetching top wallets by rewards:', error);
    return null;
  }
};

/**
 * Fetches current month rewards for a specific wallet using the betaRewardsTopListByWallet endpoint
 * 
 * @param walletAddress The wallet address
 * @returns The current month rewards data or null if there was an error
 */
export const fetchCurrentMonthWalletRewards = async (
  walletAddress: string
): Promise<{wallet: string; amount: number; numerOfGateways: number} | null> => {
  try {
    // Query for current month wallet rewards
    const queryStr = `
      query currentMonthRewards {
        stats {
          betaRewardsTopListByWallet(
            timePeriod: CURRENT_MONTH
            wallet: "${walletAddress}"
          ) {
            wallet
            amount
            numerOfGateways
          }
        }
      }
    `;

    console.log(`Fetching current month rewards for wallet ${walletAddress.slice(0, 8)}...`);
    
    const query = JSON.stringify({ query: queryStr });
    
    const response = await fetch('https://api.srcful.dev/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: query
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      throw new Error(result.errors[0].message);
    }

    // Check if the response has the expected structure
    if (!result.data || !result.data.stats || !result.data.stats.betaRewardsTopListByWallet) {
      console.error('Unexpected API response structure:', JSON.stringify(result, null, 2));
      return null;
    }

    // API returns an array, so get the first item if available
    const walletRewards = result.data.stats.betaRewardsTopListByWallet;
    console.log('Current Month Rewards API Response:', walletRewards);

    if (Array.isArray(walletRewards) && walletRewards.length > 0) {
      return walletRewards[0];
    }
    
    // If the array is empty, return a default object with zero values
    return {
      wallet: walletAddress,
      amount: 0,
      numerOfGateways: 0
    };
  } catch (error) {
    console.error('Error fetching current month rewards:', error);
    return null;
  }
}; 